import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDropEventMutation } from '~entities/events/api/drop-event';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';

interface StudentToDrop {
  studentId: string;
  studioId: string;
}

interface DropEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  students: StudentToDrop[];
  onSuccess?: () => void;
}

export function DropEventDialog({
  open,
  onOpenChange,
  eventId,
  students,
  onSuccess,
}: DropEventDialogProps) {
  const [reason, setReason] = useState('');
  const { mutateAsync: dropEvent, isPending } = useDropEventMutation();

  const handleDropEvent = async () => {
    if (!eventId || students.length === 0) {
      return;
    }
    try {
      await Promise.all(
        students.map((student) =>
          dropEvent({
            eventId,
            studentId: student.studentId,
            studioId: student.studioId,
            droppingReason: reason,
          }),
        ),
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to drop event');
    }
  };

  const studentCount = students.length;
  const isMultiple = studentCount > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Drop {isMultiple ? `${studentCount} Students` : 'Student'}</DialogTitle>
          <DialogDescription>
            Are you sure you want to drop {isMultiple ? `these ${studentCount} students` : 'this student'} from the event? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <div className='py-4'>
          <Input
            id='reason'
            placeholder='Please tell us why you are dropping from the event'
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant={'outline'}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Keep Attending
          </Button>
          <Button
            type='submit'
            variant={'destructive'}
            disabled={isPending}
            onClick={() => {
              void handleDropEvent();
            }}
          >
            {isPending && (
              <Loader2 className='animate-spin' />
            )}
            Drop {isMultiple ? `${studentCount} Students` : 'Student'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
